import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, ExclamationTriangleIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import CustomSelect from '../../components/ui/CustomSelect';

const POSInventory: React.FC = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'stock' | 'movements'>('stock');
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ product_id: '', variant_id: '', size_name: '', type: 'addition', quantity: 1, reason: '' });

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { per_page: 50 };
      if (search) params.search = search;
      if (stockFilter) params.stock_status = stockFilter;
      const response = await apiService.get('/pos/inventory', { params });
      setInventory(response.data || []);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [search, stockFilter]);

  const fetchMovements = useCallback(async () => {
    try {
      const response = await apiService.get('/pos/inventory/movements', { params: { per_page: 50 } });
      setMovements(response.data || []);
    } catch {}
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiService.get('/pos/inventory/statistics');
      setStats(response);
    } catch {}
  };

  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    if (activeTab === 'stock') {
      const timer = setTimeout(fetchInventory, 300);
      return () => clearTimeout(timer);
    } else {
      fetchMovements();
    }
  }, [activeTab, fetchInventory, fetchMovements]);

  const handleAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.post('/pos/inventory/adjust', {
        ...adjustForm,
        product_id: Number(adjustForm.product_id),
        variant_id: adjustForm.variant_id ? Number(adjustForm.variant_id) : null,
        size_name: adjustForm.size_name || null,
        quantity: Number(adjustForm.quantity),
      });
      setShowAdjustModal(false);
      setAdjustForm({ product_id: '', variant_id: '', size_name: '', type: 'addition', quantity: 1, reason: '' });
      fetchInventory();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to adjust stock');
    }
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${baseUrl}/storage/${image}`;
  };

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-600">Track stock levels, movements, and adjustments</p>
        </div>
        <button
          onClick={() => setShowAdjustModal(true)}
          className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>Adjust Stock</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Total Products</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_products}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">In Stock</p>
            <p className="text-2xl font-bold text-green-600">{stats.in_stock}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Low Stock</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.low_stock}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Out of Stock</p>
            <p className="text-2xl font-bold text-red-600">{stats.out_of_stock}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Stock Value</p>
            <p className="text-2xl font-bold text-gray-900">{Number(stats.total_stock_value).toLocaleString()} MMK</p>
          </div>
        </div>
      )}

      {/* Low-stock alert */}
      {stats?.low_stock_products?.length > 0 && (
        <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-800">
              {stats.low_stock_products.length} product{stats.low_stock_products.length > 1 ? 's' : ''} low on stock
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.low_stock_products.map((p: any) => (
              <button
                key={p.id}
                onClick={() => navigate(`/admin/inventory/${p.id}`)}
                className="text-xs bg-white border border-yellow-300 rounded-full px-3 py-1 text-yellow-800 hover:bg-yellow-100"
              >
                {p.name}: <strong>{p.in_stock}</strong>/{p.low_stock_threshold}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('stock')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stock' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Stock Levels
            </button>
            <button
              onClick={() => setActiveTab('movements')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'movements' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Stock Movements
            </button>
          </nav>
        </div>

        {/* Stock Tab */}
        {activeTab === 'stock' && (
          <div>
            <div className="p-4 border-b border-gray-200 flex gap-3">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
              <CustomSelect
                value={stockFilter}
                onChange={(val) => setStockFilter(val)}
                placeholder="All Stock"
                options={[
                  { value: '', label: 'All Stock' },
                  { value: 'in_stock', label: 'In Stock' },
                  { value: 'low', label: 'Low Stock (≤10)' },
                  { value: 'out', label: 'Out of Stock' },
                ]}
                className="w-44"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1000px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sell Price</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variants</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : inventory.length === 0 ? (
                    <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">No products found</td></tr>
                  ) : (
                    inventory.map((item: any) => (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/inventory/${item.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.image && <img src={getImageUrl(item.image) || undefined} className="w-8 h-8 rounded object-cover mr-3 flex-shrink-0" alt="" />}
                            <span className="text-sm font-medium text-gray-900">{item.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">{item.product_code}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{item.category || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-right">{Number(item.cost_price).toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">{Number(item.price).toLocaleString()}</td>
                        <td className="px-6 py-4 text-center">
                          <span
                            title={item.low_stock_threshold != null ? `Low-stock threshold: ${item.low_stock_threshold}` : undefined}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              (item.stock_status === 'out' || item.total_stock <= 0) ? 'bg-red-100 text-red-800' :
                              item.stock_status === 'low' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}
                          >
                            {(item.stock_status === 'out' || item.stock_status === 'low' || item.total_stock <= 0) && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                            {item.total_stock}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 whitespace-nowrap">
                          {item.stock_details?.slice(0, 3).map((d: any, i: number) => (
                            <span key={i} className="mr-2 inline-block">{d.color}/{d.size}: <strong>{d.stock}</strong></span>
                          ))}
                          {item.stock_details?.length > 3 && <span className="text-gray-400">+{item.stock_details.length - 3}</span>}
                        </td>
                        <td className="px-6 py-4 text-center sticky right-0 bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/inventory/${item.id}`); }}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm"
                          >
                            <EyeIcon className="h-4 w-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Movements Tab */}
        {activeTab === 'movements' && (
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Before</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">After</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {movements.map((m: any) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{m.product?.name}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        m.quantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>{m.type.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-bold">
                      <span className={m.quantity > 0 ? 'text-green-600' : 'text-red-600'}>
                        {m.quantity > 0 ? '+' : ''}{m.quantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">{m.quantity_before}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900 font-medium">{m.quantity_after}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">{m.reason || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{m.created_by_user?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adjust Stock Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Stock Adjustment</h3>
              <button onClick={() => setShowAdjustModal(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <form onSubmit={handleAdjust} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <select required value={adjustForm.product_id} onChange={(e) => setAdjustForm(f => ({ ...f, product_id: e.target.value, variant_id: '', size_name: '' }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">Select Product</option>
                  {inventory.map((p: any) => <option key={p.id} value={p.id}>{p.name} (Stock: {p.total_stock})</option>)}
                </select>
              </div>
              {adjustForm.product_id && (() => {
                const product = inventory.find((p: any) => p.id === Number(adjustForm.product_id));
                const details = product?.stock_details || [];
                // Get unique variants
                const variants = details.reduce((acc: any[], d: any) => {
                  if (!acc.find((v: any) => v.variant_id === d.variant_id)) {
                    acc.push({ variant_id: d.variant_id, color: d.color, sku: d.sku });
                  }
                  return acc;
                }, []);
                if (variants.length === 0) return null;
                return (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant (Color)</label>
                      <select value={adjustForm.variant_id} onChange={(e) => setAdjustForm(f => ({ ...f, variant_id: e.target.value, size_name: '' }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        <option value="">Select Variant</option>
                        {variants.map((v: any) => <option key={v.variant_id} value={v.variant_id}>{v.color} ({v.sku})</option>)}
                      </select>
                    </div>
                    {adjustForm.variant_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                        <select value={adjustForm.size_name} onChange={(e) => setAdjustForm(f => ({ ...f, size_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                          <option value="">Select Size</option>
                          {details.filter((d: any) => String(d.variant_id) === adjustForm.variant_id).map((d: any) => (
                            <option key={d.size} value={d.size}>{d.size} (current stock: {d.stock})</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                );
              })()}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type *</label>
                <select required value={adjustForm.type} onChange={(e) => setAdjustForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="addition">Addition (+)</option>
                  <option value="subtraction">Subtraction (-)</option>
                  <option value="damage">Damage (-)</option>
                  <option value="correction">Correction</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                <input type="number" required min="1" value={adjustForm.quantity} onChange={(e) => setAdjustForm(f => ({ ...f, quantity: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <input type="text" required value={adjustForm.reason} onChange={(e) => setAdjustForm(f => ({ ...f, reason: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setShowAdjustModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">Apply Adjustment</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSInventory;
